import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Sparkles, RefreshCw, Newspaper, Mic, ChevronRight, Calendar, FileText, Instagram, ExternalLink, Users, TrendingUp, Zap } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const fmtDate = (d) => { try { return d ? format(parseISO(d), 'MMMM d, yyyy') : '-'; } catch { return '-'; } };

function MarkdownContent({ content }) {
  if (!content) return null;
  const lines = content.split('\n');
  return (
    <div className="prose-content">
      {lines.map((line, i) => {
        if (line.startsWith('## ')) return <h2 key={i}>{line.slice(3)}</h2>;
        if (line.startsWith('### ')) return <h3 key={i}>{line.slice(4)}</h3>;
        if (line.startsWith('# ')) return <h1 key={i}>{line.slice(2)}</h1>;
        if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i}>{line.slice(2)}</li>;
        if (line.trim() === '') return <br key={i} />;
        return <p key={i}>{line}</p>;
      })}
    </div>
  );
}

export default function IntelligenceHub() {
  const [briefings, setBriefings] = useState([]);
  const [reports, setReports] = useState([]);
  const [memos, setMemos] = useState([]);
  const [igIntel, setIgIntel] = useState(null);
  const [selectedBriefing, setSelectedBriefing] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedMemo, setSelectedMemo] = useState(null);
  const [generatingBriefing, setGeneratingBriefing] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [processingMemo, setProcessingMemo] = useState(false);
  const [runningIg, setRunningIg] = useState(false);
  const [memoText, setMemoText] = useState('');
  const [memoTitle, setMemoTitle] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [bRes, rRes, mRes, igRes] = await Promise.all([
        api.get('/api/intel/briefings'),
        api.get('/api/intel/reports'),
        api.get('/api/intel/voice-memos'),
        api.get('/api/intel/instagram/latest').catch(() => ({ data: null })),
      ]);
      setBriefings(bRes.data || []);
      setReports(rRes.data || []);
      setMemos(mRes.data || []);
      setIgIntel(igRes.data || null);
      if (bRes.data?.length > 0) setSelectedBriefing(bRes.data[0]);
      if (rRes.data?.length > 0) setSelectedReport(rRes.data[0]);
      if (mRes.data?.length > 0) setSelectedMemo(mRes.data[0]);
    } catch { toast.error('Failed to load intelligence'); }
  };

  const generateBriefing = async () => {
    setGeneratingBriefing(true);
    try {
      const res = await api.post('/api/intel/briefings/generate');
      toast.success('Morning Briefing generated!');
      setBriefings(prev => [res.data, ...prev]);
      setSelectedBriefing(res.data);
    } catch { toast.error('Failed to generate briefing'); }
    finally { setGeneratingBriefing(false); }
  };

  const generateReport = async () => {
    setGeneratingReport(true);
    try {
      const res = await api.post('/api/intel/reports/generate');
      toast.success('Market Report generated!');
      setReports(prev => [res.data, ...prev]);
      setSelectedReport(res.data);
    } catch { toast.error('Failed to generate report'); }
    finally { setGeneratingReport(false); }
  };

  const processMemo = async () => {
    if (!memoText.trim()) { toast.error('Enter memo text first'); return; }
    setProcessingMemo(true);
    try {
      const res = await api.post('/api/intel/voice-memo', { text: memoText, title: memoTitle || undefined });
      if (!res.data || !res.data.id) throw new Error('Empty response from server');
      toast.success('Memo processed and intelligence extracted!');
      setMemos(prev => [res.data, ...prev]);
      setSelectedMemo(res.data);
      setMemoText('');
      setMemoTitle('');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to process memo — check your connection and try again';
      toast.error(msg);
    }
    finally { setProcessingMemo(false); }
  };

  const runIgBot = async () => {
    setRunningIg(true);
    try {
      await api.post('/api/intel/instagram/run');
      toast.success('Instagram Intel bot started — scanning your following list (2-3 min)');
      setTimeout(async () => {
        const res = await api.get('/api/intel/instagram/latest').catch(() => ({ data: null }));
        if (res.data?.intel_items?.length > 0) {
          setIgIntel(res.data);
          toast.success(`Found ${res.data.intel_items.length} intel items!`);
        }
        setRunningIg(false);
      }, 150000); // check after 2.5 min
    } catch { toast.error('Failed to start bot'); setRunningIg(false); }
  };

  const INTEL_TYPE_COLORS = {
    restaurant_opening: 'bg-amber-50 border-amber-200 text-amber-700',
    event:              'bg-purple-50 border-purple-200 text-purple-700',
    real_estate:        'bg-teal-50 border-teal-200 text-teal-700',
    hospitality_trend:  'bg-blue-50 border-blue-200 text-blue-700',
    competitor:         'bg-red-50 border-red-200 text-red-700',
    opportunity:        'bg-green-50 border-green-200 text-green-700',
    general:            'bg-muted border-border text-muted-foreground',
  };

  return (
    <div className="space-y-5">
      <div className="page-header">
        <h1 className="page-title">Intelligence Hub</h1>
        <p className="page-subtitle">Morning briefings, market reports, Instagram intel &amp; field note processing</p>
      </div>

      <Tabs defaultValue="briefing">
        <TabsList className="mb-4">
          <TabsTrigger value="briefing" className="flex items-center gap-1.5">
            <Newspaper size={14} />Morning Briefing
          </TabsTrigger>
          <TabsTrigger value="report" className="flex items-center gap-1.5">
            <FileText size={14} />Market Report
          </TabsTrigger>
          <TabsTrigger value="instagram" className="flex items-center gap-1.5">
            <Instagram size={14} />Instagram Intel
            {igIntel?.intel_items?.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-700 text-xs font-bold">
                {igIntel.intel_items.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="memo" className="flex items-center gap-1.5">
            <Mic size={14} />Field Notes
          </TabsTrigger>
        </TabsList>

        {/* Morning Briefing */}
        <TabsContent value="briefing">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">Briefings</p>
                <Button size="sm" onClick={generateBriefing} disabled={generatingBriefing} className="bg-primary" data-testid="generate-briefing-btn">
                  <Sparkles size={13} className="mr-1.5" />
                  {generatingBriefing ? 'Generating...' : 'Generate'}
                </Button>
              </div>
              {briefings.length === 0 ? (
                <Card><CardContent className="text-center py-8">
                  <Newspaper size={32} className="mx-auto mb-2 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No briefings yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Generate your first morning briefing</p>
                </CardContent></Card>
              ) : (
                <div className="space-y-2">
                  {briefings.map(b => (
                    <button
                      key={b.id}
                      onClick={() => setSelectedBriefing(b)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedBriefing?.id === b.id
                          ? 'border-primary/40 bg-accent/20'
                          : 'border-border bg-card hover:bg-muted'
                      }`}
                    >
                      <div className="text-sm font-medium">{b.title || `Briefing - ${fmtDate(b.created_at)}`}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(b.created_at)}</div>
                      {b.stats && (
                        <div className="flex gap-2 mt-1.5 flex-wrap">
                          {b.stats.new_contacts > 0 && <Badge variant="secondary" className="text-xs">{b.stats.new_contacts} new contacts</Badge>}
                          {b.stats.due_followups > 0 && <Badge variant="secondary" className="text-xs">{b.stats.due_followups} due</Badge>}
                          {b.stats.news_items > 0 && <Badge variant="secondary" className="text-xs bg-primary/10 text-primary border-primary/20">{b.stats.news_items} news</Badge>}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="lg:col-span-2">
              {selectedBriefing ? (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                          {selectedBriefing.title || `Morning Briefing`}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">{fmtDate(selectedBriefing.created_at)}</p>
                      </div>
                      <div className="flex gap-2 flex-wrap justify-end">
                        {selectedBriefing.stats?.due_followups > 0 && (
                          <Badge variant="secondary" className="text-xs">{selectedBriefing.stats.due_followups} follow-ups due</Badge>
                        )}
                        {selectedBriefing.stats?.news_items > 0 && (
                          <Badge className="text-xs bg-primary/10 text-primary border border-primary/20">
                            {selectedBriefing.stats.news_items} live news items
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Live news cards pulled into briefing */}
                    {selectedBriefing.news_articles?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                          <Newspaper size={11} /> Live Hamptons News — Included in Briefing
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {selectedBriefing.news_articles.map((article, i) => (
                            <div key={i} className="p-3 rounded-lg bg-primary/5 border border-primary/15">
                              <div className="text-xs font-semibold text-foreground leading-snug">{article.title}</div>
                              {article.summary && <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{article.summary}</p>}
                              {article.relevance && (
                                <p className="text-xs text-primary mt-1 font-medium">{article.relevance}</p>
                              )}
                            </div>
                          ))}
                        </div>
                        {selectedBriefing.news_citations?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {selectedBriefing.news_citations.slice(0, 4).map((url, i) => {
                              let domain = url; try { domain = new URL(url).hostname.replace('www.', ''); } catch {}
                              return (
                                <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                                  className="text-xs px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground hover:text-foreground transition-colors">
                                  {domain}
                                </a>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    <ScrollArea className="h-[420px] pr-2">
                      <MarkdownContent content={selectedBriefing.content} />
                    </ScrollArea>
                  </CardContent>
                </Card>
              ) : (
                <Card><CardContent className="text-center py-16">
                  <Sparkles size={40} className="mx-auto mb-3 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">Generate your morning briefing to see it here</p>
                  <Button onClick={generateBriefing} disabled={generatingBriefing} className="mt-4 bg-primary">
                    <Sparkles size={14} className="mr-1.5" /> Generate Morning Briefing
                  </Button>
                </CardContent></Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Market Report */}
        <TabsContent value="report">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">Reports</p>
                <Button size="sm" onClick={generateReport} disabled={generatingReport} className="bg-primary" data-testid="generate-report-btn">
                  <Sparkles size={13} className="mr-1.5" />
                  {generatingReport ? 'Generating...' : 'Generate'}
                </Button>
              </div>
              {reports.length === 0 ? (
                <Card><CardContent className="text-center py-8">
                  <FileText size={32} className="mx-auto mb-2 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No reports yet</p>
                </CardContent></Card>
              ) : (
                <div className="space-y-2">
                  {reports.map(r => (
                    <button
                      key={r.id}
                      onClick={() => setSelectedReport(r)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedReport?.id === r.id ? 'border-primary/40 bg-accent/20' : 'border-border bg-card hover:bg-muted'
                      }`}
                    >
                      <div className="text-sm font-medium">{r.title || `Report - ${fmtDate(r.created_at)}`}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Week ending {fmtDate(r.created_at)}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="lg:col-span-2">
              {selectedReport ? (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {selectedReport.title || 'Weekly Market Report'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[500px] pr-2">
                      <MarkdownContent content={selectedReport.content} />
                    </ScrollArea>
                  </CardContent>
                </Card>
              ) : (
                <Card><CardContent className="text-center py-16">
                  <FileText size={40} className="mx-auto mb-3 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">Generate your weekly market report</p>
                  <Button onClick={generateReport} disabled={generatingReport} className="mt-4 bg-primary">
                    <Sparkles size={14} className="mr-1.5" /> Generate Market Report
                  </Button>
                </CardContent></Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Field Notes / Voice Memos */}
        <TabsContent value="memo">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Process Field Note</CardTitle>
                <p className="text-sm text-muted-foreground">Paste your field notes or voice memo transcript. Claude will extract contacts, restaurants, spaces, and follow-up actions.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Note Title (optional)</Label>
                  <Input value={memoTitle} onChange={e => setMemoTitle(e.target.value)} placeholder="Field Visit - Southampton, June 15" data-testid="memo-title-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Note Content *</Label>
                  <Textarea
                    value={memoText}
                    onChange={e => setMemoText(e.target.value)}
                    placeholder="Met John Smith at The Barn today. He's the GM. Great energy. They're at 80% capacity on weekends. Mentioned a new space opening on Main St next to the yoga studio. Need to follow up with the owner Sarah about the pop-up opportunity..."
                    rows={8}
                    data-testid="memo-text-input"
                  />
                </div>
                <Button
                  onClick={processMemo}
                  disabled={processingMemo || !memoText.trim()}
                  className="w-full bg-primary"
                  data-testid="process-memo-btn"
                >
                  <Sparkles size={14} className="mr-1.5" />
                  {processingMemo ? 'Processing...' : 'Extract Intelligence'}
                </Button>
              </CardContent>
            </Card>

            <div className="space-y-3">
              <p className="text-sm font-semibold">Processed Notes</p>
              {memos.length === 0 ? (
                <Card><CardContent className="text-center py-8">
                  <Mic size={32} className="mx-auto mb-2 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No processed notes yet</p>
                </CardContent></Card>
              ) : (
                <div className="space-y-3">
                  {memos.slice(0, 5).map(m => (
                    <Card
                      key={m.id}
                      className={`cursor-pointer transition-colors ${selectedMemo?.id === m.id ? 'border-primary/40' : ''}`}
                      onClick={() => setSelectedMemo(m)}
                    >
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">{m.title}</CardTitle>
                        <p className="text-xs text-muted-foreground">{fmtDate(m.created_at)}</p>
                      </CardHeader>
                      {selectedMemo?.id === m.id && (
                        <CardContent className="pt-0">
                          <ScrollArea className="h-48">
                            <div className="text-sm text-foreground prose-content">{m.extraction}</div>
                          </ScrollArea>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ── INSTAGRAM INTEL ── */}
        <TabsContent value="instagram">
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold flex items-center gap-2">
                  <Instagram size={15} className="text-pink-500" />
                  Instagram Intel — @hamptonsxp Following List
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Scans accounts you follow for Hamptons hospitality intelligence · runs daily 6:30 AM ET · no Graph API
                </p>
              </div>
              <Button onClick={runIgBot} disabled={runningIg} className="bg-gradient-to-r from-pink-500 to-purple-600 text-white" data-testid="run-ig-intel-btn">
                {runningIg ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Scanning (~2 min)...
                  </span>
                ) : (
                  <span className="flex items-center gap-2"><Instagram size={14} /> Run Bot Now</span>
                )}
              </Button>
            </div>

            {/* Stats bar */}
            {igIntel && (
              <div className="flex items-center gap-4 px-4 py-2.5 rounded-xl bg-pink-50 border border-pink-200">
                <Instagram size={14} className="text-pink-500 flex-shrink-0" />
                <span className="text-xs font-semibold text-pink-700">Last run: {igIntel.run_date}</span>
                <span className="text-xs text-muted-foreground">·</span>
                <span className="text-xs text-muted-foreground"><Users size={10} className="inline mr-1" />{igIntel.accounts_scanned} accounts scanned</span>
                <span className="text-xs text-muted-foreground">·</span>
                <span className="text-xs text-muted-foreground"><TrendingUp size={10} className="inline mr-1" />{igIntel.posts_analyzed} posts analysed</span>
                <span className="text-xs text-muted-foreground">·</span>
                <span className="text-xs font-semibold text-pink-700"><Zap size={10} className="inline mr-1" />{igIntel.intel_items?.length || 0} intel items found</span>
              </div>
            )}

            {/* Intel cards */}
            {!igIntel || !igIntel.intel_items?.length ? (
              <Card>
                <CardContent className="text-center py-16">
                  <Instagram size={48} className="mx-auto mb-4 text-muted-foreground/20" />
                  <h3 className="text-lg font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>No Instagram Intel yet</h3>
                  <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
                    Click "Run Bot Now" to scan @hamptonsxp's following list for Hamptons hospitality intelligence.
                    Results are automatically added to your Morning Briefing.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {igIntel.intel_items.map((item, i) => {
                  const typeStyle = INTEL_TYPE_COLORS[item.intel_type] || INTEL_TYPE_COLORS.general;
                  return (
                    <Card key={i} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <a href={`https://www.instagram.com/${item.account?.replace('@','')}/`}
                                target="_blank" rel="noopener noreferrer"
                                className="text-xs font-semibold text-pink-600 hover:underline flex items-center gap-1">
                                <Instagram size={11} />{item.account}
                              </a>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${typeStyle}`}>
                                {(item.intel_type || 'general').replace('_', ' ')}
                              </span>
                            </div>
                            <CardTitle className="text-sm leading-snug" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                              {item.headline}
                            </CardTitle>
                          </div>
                          {item.engagement && (
                            <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium ${
                              item.engagement === 'high' ? 'bg-green-100 text-green-700' :
                              item.engagement === 'medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-muted text-muted-foreground'
                            }`}>{item.engagement}</span>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-2">
                        <p className="text-sm text-muted-foreground leading-relaxed">{item.detail}</p>
                        {item.action && (
                          <div className="flex items-start gap-2 p-2 rounded-lg bg-primary/5 border border-primary/15">
                            <Zap size={11} className="text-primary mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-primary font-medium">{item.action}</p>
                          </div>
                        )}
                        {item.post_url && (
                          <a href={item.post_url} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                            <ExternalLink size={10} /> View post
                          </a>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
